import { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import logo from "@/assets/logo.svg";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguageRouting } from "@/hooks/useLanguageRouting";

const Navbar = () => {
  const { t } = useTranslation('common');
  const { getLocalizedLink } = useLanguageRouting();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/what-is-nearshoring", label: t('nav.whatIsNearshoring') },
    { href: "/nearshoring-vs-offshoring-vs-outsourcing", label: t('nav.nearshoringVsOffshoring') },
    { href: "/companies", label: t('nav.companies') },
    { href: "/blog", label: t('nav.blog') },
  ];

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/70 backdrop-blur-xl backdrop-saturate-150 border-b border-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to={getLocalizedLink("/")} className="flex items-center gap-2.5">
            <img src={logo} alt="Nearshore.Africa Logo" className="h-8 w-8 rounded-[7px]" width={32} height={32} />
            <div className="text-base font-semibold tracking-tight text-foreground">
              Nearshore.Africa
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={getLocalizedLink(link.href)}
                className="text-sm text-foreground/70 hover:text-foreground hover:bg-muted/60 transition-all duration-200 font-medium px-3 py-2 rounded-full"
              >
                {link.label}
              </Link>
            ))}
            <div className="ml-2">
              <LanguageSwitcher />
            </div>
            <Button size="sm" className="ml-2" asChild>
              <a href="https://www.evolvice.de/en" target="_blank" rel="noopener noreferrer">
                {t('nav.getStarted')}
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-full hover:bg-muted/60 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/60 animate-fade-in">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={getLocalizedLink(link.href)}
                  className="text-sm text-foreground/80 hover:text-foreground hover:bg-muted/60 transition-all duration-200 font-medium px-3 py-2.5 rounded-lg"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-3 py-2">
                <LanguageSwitcher />
              </div>
              <Button className="w-full mt-2" asChild>
                <a href="https://www.evolvice.de/en" target="_blank" rel="noopener noreferrer">
                  {t('nav.getStarted')}
                </a>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
